import { glow } from "@/vessels";
import { playScarySound } from "@/eatSounds";
import { humFlags } from "@/humSurprise";
import { makeBoom, drawBoom } from "@/crashFx";
const orangeGap = () => 120000 + Math.random() * 120000;
export const RECORD_KEY = "sleep-site-snake-record";
export const snakeRecord = () => Number(localStorage.getItem(RECORD_KEY)) || 0;
const PURPLE = 3;

const TAU = Math.PI * 2;
const rand = (a, b) => a + Math.random() * (b - a);
const fade = (ctx, w, h, a, c = "8,8,10") => {
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = `rgba(${c},${a})`;
  ctx.fillRect(0, 0, w, h);
};

const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1]];

const newSnake = (cols, rows) => {
  const x = Math.floor(cols / 2), y = Math.floor(rows / 2);
  return { body: Array.from({ length: 6 }, (_, i) => ({ x: x - i, y })), dir: [1, 0], grow: 0 };
};

const crash = (s) => {
  const { cell, cols, rows } = s;
  const ox = (s.w - cols * cell) / 2, oy = (s.h - rows * cell) / 2;
  const hd = s.snake.body[0];
  const cx = ox + (hd.x + 0.5) * cell, cy = oy + (hd.y + 0.5) * cell;
  s.boom = makeBoom(s.w, s.h, cx, cy);
  window.__snakeCrashes = (window.__snakeCrashes || 0) + 1;
  window.__snakeLastLength = s.snake.body.length;
  window.dispatchEvent(new CustomEvent("snake-crash"));
};

const runBoom = (ctx, w, h, s, k) => {
  if (!drawBoom(ctx, w, h, s.boom, k)) return;
  s.boom = null;
  s.snake = newSnake(s.cols, s.rows);
  s.food = placeFood(s);
  s.purple = Array.from({ length: PURPLE }, () => placeFood(s));
};

const placeFood = (s) => {
  const free = s.cols * s.rows - s.snake.body.length;
  if (free <= 0) return { x: -1, y: -1 };
  let f;
  do f = { x: Math.floor(rand(0, s.cols)), y: Math.floor(rand(0, s.rows)) };
  while (s.snake.body.some((b) => b.x === f.x && b.y === f.y));
  return f;
};

const slipGap = () => rand(45000, 100000);
const slippery = (s) => performance.now() < s.slipUntil;
const startSlip = (s) => { s.slipUntil = performance.now() + rand(10000, 16000); s.nextSlip = s.slipUntil + slipGap(); window.__snakeSlips = (window.__snakeSlips || 0) + 1; };

const stepSnake = (s) => {
  const { body, dir } = s.snake;
  const head = body[0];
  const wrap = (x, n) => (x + n) % n;
  const all = DIRS.filter(([dx, dy]) => dx !== -dir[0] || dy !== -dir[1])
    .map(([dx, dy]) => ({ d: [dx, dy], p: { x: wrap(head.x + dx, s.cols), y: wrap(head.y + dy, s.rows) } }));
  const hits = (p) => body.slice(0, -1).some((b) => b.x === p.x && b.y === p.y);
  const opts = all.filter(({ p }) => !hits(p));
  const careless = body.length > 150 && Math.random() < 0.0003;
  if (!opts.length || s.food.x < 0 || (careless && all.some(({ p }) => hits(p)))) {
    crash(s);
    return;
  }
  if (!s.orange && performance.now() > s.nextOrange) s.orange = placeFood(s);
  const target = [s.food, ...s.purple, s.orange].filter((f) => f && f.x >= 0).reduce((best, f) => {
    const d = Math.abs(head.x - f.x) + Math.abs(head.y - f.y);
    return d < best.d ? { f, d } : best;
  }, { f: s.food, d: Infinity }).f;
  const dist = (p) => Math.abs(p.x - target.x) + Math.abs(p.y - target.y);
  opts.sort((a, b) => dist(a.p) - dist(b.p) + (Math.random() - 0.5) * 0.6);
  let { d, p } = opts[0];
  const straight = opts.find((o) => o.d[0] === dir[0] && o.d[1] === dir[1]);
  if (slippery(s) && straight && straight !== opts[0]) {
    if (s.skid > 0 || Math.random() < 0.75) {
      s.skid = s.skid > 0 ? s.skid - 1 : Math.floor(rand(1, 4));
      ({ d, p } = straight);
      window.__snakeSkids = (window.__snakeSkids || 0) + 1;
    }
  } else s.skid = 0;
  s.snake.dir = d;
  body.unshift(p);
  if (p.x === s.food.x && p.y === s.food.y) {
    s.snake.grow += 4;
    s.food = placeFood(s);
  }
  if (s.orange && p.x === s.orange.x && p.y === s.orange.y) {
    s.snake.grow += 3;
    s.orange = null;
    s.nextOrange = performance.now() + orangeGap();
    window.__orangeEaten = (window.__orangeEaten || 0) + 1;
    if (!humFlags.quiet) {
      playScarySound();
      window.dispatchEvent(new CustomEvent("scary-flash"));
    }
  }
  const pi = s.purple.findIndex((f) => f.x === p.x && f.y === p.y);
  if (pi >= 0) {
    s.snake.grow += 2;
    s.purple[pi] = placeFood(s);
    window.__purpleEaten = (window.__purpleEaten || 0) + 1;
  }
  if (s.snake.grow > 0) s.snake.grow--;
  else body.pop();
};

export const snake = {
  id: "snake",
  name: "Snake",
  subtitle: "A neon snake hunting glowing snacks",
  init(w, h) {
    const cell = Math.max(18, Math.round(Math.min(w, h) / 30));
    const s = { w, h, cell, cols: Math.floor(w / cell), rows: Math.floor(h / cell), acc: 0, boom: null };
    s.snake = newSnake(s.cols, s.rows);
    s.food = placeFood(s);
    s.purple = Array.from({ length: PURPLE }, () => placeFood(s));
    s.orange = null;
    s.nextOrange = performance.now() + orangeGap();
    window.__snakeCrash = () => crash(s);
    window.__spawnOrange = () => { s.orange = placeFood(s); };
    s.slipUntil = 0;
    s.nextSlip = performance.now() + slipGap();
    s.skid = 0;
    window.__snakeSlipNow = () => startSlip(s);
    window.__snakeState = s;
    return s;
  },
  draw(ctx, w, h, t, s, p, k) {
    if (s.boom) { runBoom(ctx, w, h, s, k); return; }
    fade(ctx, w, h, 0.35, "4,10,6");
    if (performance.now() > s.nextSlip) startSlip(s);
    const slick = slippery(s);
    window.__snakeSlippery = slick;
    s.acc += k * (slick ? 0.55 : 0.4);
    while (s.acc >= 1 && !s.boom) { s.acc -= 1; stepSnake(s); }
    if (s.boom) return;
    window.__snakeLength = s.snake.body.length;
    if (s.snake.body.length > snakeRecord()) {
      localStorage.setItem(RECORD_KEY, String(s.snake.body.length));
      window.dispatchEvent(new CustomEvent("snake-record"));
    }
    const { cell } = s;
    const ox = (w - s.cols * cell) / 2, oy = (h - s.rows * cell) / 2;
    ctx.globalCompositeOperation = "lighter";
    const fx = ox + (s.food.x + 0.5) * cell, fy = oy + (s.food.y + 0.5) * cell;
    glow(ctx, fx, fy, cell * (1.2 + 0.3 * Math.sin(t * 6)), "330,100%,60%", 0.5);
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#ff4fd8";
    ctx.fillRect(fx - cell * 0.25, fy - cell * 0.25, cell * 0.5, cell * 0.5);
    if (s.orange && s.orange.x >= 0) {
      const ox2 = ox + (s.orange.x + 0.5) * cell, oy2 = oy + (s.orange.y + 0.5) * cell;
      ctx.globalCompositeOperation = "lighter";
      glow(ctx, ox2, oy2, cell * (1.4 + 0.5 * Math.abs(Math.sin(t * 7))), "24,100%,55%", 0.7);
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#ff7a00";
      ctx.beginPath();
      ctx.arc(ox2, oy2, cell * 0.34, 0, TAU);
      ctx.fill();
    }
    s.purple.forEach((pf, i) => {
      if (pf.x < 0) return;
      const px = ox + (pf.x + 0.5) * cell, py = oy + (pf.y + 0.5) * cell;
      ctx.globalCompositeOperation = "lighter";
      glow(ctx, px, py, cell * (1 + 0.25 * Math.sin(t * 4 + i * 2)), "275,100%,62%", 0.55);
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#b44dff";
      ctx.beginPath();
      ctx.arc(px, py, cell * 0.28, 0, TAU);
      ctx.fill();
    });
    const n = s.snake.body.length;
    s.snake.body.forEach((b, i) => {
      const l = 62 - (i / n) * 30;
      ctx.fillStyle = `hsl(${120 + Math.sin(t + i * 0.2) * 20},100%,${l}%)`;
      const pad = i === 0 ? 1 : 3;
      ctx.fillRect(ox + b.x * cell + pad, oy + b.y * cell + pad, cell - pad * 2, cell - pad * 2);
      if (slick) {
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.fillRect(ox + b.x * cell + pad + 1, oy + b.y * cell + pad + 1, (cell - pad * 2) * 0.45, (cell - pad * 2) * 0.22);
      }
    });
    if (slick) {
      ctx.globalCompositeOperation = "lighter";
      s.snake.body.forEach((b, i) => { if (i % 3 === 0) glow(ctx, ox + (b.x + 0.5) * cell, oy + (b.y + 0.5) * cell, cell * 0.9, "160,90%,70%", 0.12); });
      ctx.globalCompositeOperation = "source-over";
    }
    const hd = s.snake.body[0];
    ctx.globalCompositeOperation = "lighter";
    glow(ctx, ox + (hd.x + 0.5) * cell, oy + (hd.y + 0.5) * cell, cell * 2, "120,100%,55%", 0.25 + p * 0.2);
  },
};

const makeWorm = (w, h) => {
  const x = rand(0, w), y = rand(0, h);
  const r = rand(9, 17) * Math.max(0.6, Math.min(w, h) / 900);
  const angle = rand(0, TAU);
  return { r, angle, speed: rand(0.5, 1.1), ph: rand(0, TAU), hue: rand(345, 362) % 360,
    pts: Array.from({ length: 34 }, (_, i) => ({ x: x - Math.cos(angle) * i * r * 0.75, y: y - Math.sin(angle) * i * r * 0.75 })) };
};

const soil = (w, h) => {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const g = c.getContext("2d");
  g.fillStyle = "#1a110b";
  g.fillRect(0, 0, w, h);
  for (let i = 0; i < (w * h) / 900; i++) {
    g.fillStyle = `rgba(${rand(60, 110)},${rand(40, 70)},${rand(20, 40)},${rand(0.15, 0.5)})`;
    g.beginPath();
    g.arc(rand(0, w), rand(0, h), rand(0.6, 3.2), 0, TAU);
    g.fill();
  }
  return c;
};

export const worm = {
  id: "worm",
  name: "Worm",
  subtitle: "Pink earthworms wriggling through dark soil",
  init(w, h) {
    return { bg: soil(w, h), worms: Array.from({ length: 6 }, () => makeWorm(w, h)) };
  },
  draw(ctx, w, h, t, s, p, k) {
    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(s.bg, 0, 0);
    s.worms.forEach((wm, idx) => {
      wm.angle += (Math.sin(t * 0.8 + wm.ph) * 0.03 + rand(-0.01, 0.01)) * k;
      const head = wm.pts[0];
      head.x += Math.cos(wm.angle) * wm.speed * k;
      head.y += Math.sin(wm.angle) * wm.speed * k;
      const m = wm.r * 40;
      if (head.x < -m || head.x > w + m || head.y < -m || head.y > h + m) s.worms[idx] = makeWorm(w, h);
      const gap = wm.r * 0.75;
      for (let i = 1; i < wm.pts.length; i++) {
        const a = wm.pts[i - 1], b = wm.pts[i];
        const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy) || 1;
        if (d > gap) { b.x = a.x + (dx / d) * gap; b.y = a.y + (dy / d) * gap; }
      }
      for (let i = wm.pts.length - 1; i >= 0; i--) {
        const pt = wm.pts[i];
        const taper = 0.55 + 0.45 * Math.sin((i / wm.pts.length) * Math.PI * 0.9 + 0.25);
        const r = wm.r * taper * (1 + 0.16 * Math.sin(i * 0.55 - t * 7 + wm.ph));
        const band = i >= 7 && i <= 10;
        const l = band ? 48 : 62 + (i % 2 ? 4 : -3) + p * 8;
        const g = ctx.createRadialGradient(pt.x - r * 0.3, pt.y - r * 0.3, r * 0.1, pt.x, pt.y, r);
        g.addColorStop(0, `hsl(${wm.hue},${band ? 55 : 45}%,${l + 14}%)`);
        g.addColorStop(1, `hsl(${wm.hue},40%,${l - 22}%)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, r, 0, TAU);
        ctx.fill();
      }
    });
  },
};
